import { useContext, useRef, type MouseEvent } from "react";
import { EditorContent } from "@tiptap/react";
import { HTMLContainer } from "./canvas";
import { useEditor } from "./canvas";
import { useValue } from "@tldraw/state-react";
import { ArrowUpRight, Check } from "lucide-react";
import type { BoardDocument } from "../shared/schema";
import { DocumentAccess } from "./DocumentAccess";
import { DocumentFormatting } from "./DocumentFormatting";
import {
  nativeDocumentHtml,
  nativeDocumentPlaintext,
} from "./document-richtext";
import { useDocumentWriting } from "./useDocumentWriting";
import {
  createWorkspaceDocument,
  documentMarkdown,
} from "./workspace-documents";

export function CanvasDocument({
  shape,
  continues,
}: {
  shape: BoardDocument;
  continues: boolean;
}) {
  const editor = useEditor();
  const access = useContext(DocumentAccess);
  const editing = useValue(
    "canvas document editing",
    () => editor.getEditingShapeId() === shape.id,
    [editor, shape.id],
  );
  const focusPoint = useRef<
    { x: number; y: number; bodyTop?: number } | undefined
  >(undefined);
  const titleFirst = useRef(shape.props.name === "Untitled document");
  function start(event?: MouseEvent) {
    if (
      !access.canEdit ||
      !access.connected ||
      shape.isLocked ||
      editor.getIsReadonly()
    )
      return;
    if (event) {
      event.stopPropagation();
      focusPoint.current = { x: event.clientX, y: event.clientY };
      titleFirst.current =
        (event.target as HTMLElement).closest(".native-document-title") !==
        null;
    } else {
      focusPoint.current = undefined;
      titleFirst.current =
        shape.props.name === "Untitled document" &&
        !nativeDocumentPlaintext(shape.props.richText);
    }
    editor.select(shape.id);
    editor.setEditingShape(shape.id);
    editor.setCurrentTool("select.editing_shape");
  }
  function expand() {
    editor.setCurrentTool("select.idle");
    window.dispatchEvent(
      new CustomEvent("calque:open-document", { detail: shape.id }),
    );
  }
  return (
    <HTMLContainer
      className={`board-document native-document ${editing ? "is-writing" : ""}`}
      style={{
        width: shape.props.w,
        height: shape.props.h,
        pointerEvents: "all",
      }}
      onPointerDownCapture={(event) => {
        if (editing) return;
        const preview = event.currentTarget.querySelector(
          ".native-document-preview",
        );
        focusPoint.current = {
          x: event.clientX,
          y: event.clientY,
          bodyTop: preview
            ? preview.getBoundingClientRect().top + 24 * editor.getZoomLevel()
            : undefined,
        };
        titleFirst.current =
          (event.target as HTMLElement).closest(".native-document-title") !==
          null;
      }}
      onDoubleClick={editing ? undefined : start}
    >
      {editing ? (
        <CanvasDocumentWriter
          shape={shape}
          focusPoint={focusPoint.current}
          titleFirst={titleFirst.current}
          onExpand={expand}
        />
      ) : (
        <>
          <div className="native-document-masthead">
            <span>
              {String(Number(shape.meta.order ?? 0) + 1).padStart(2, "0")}
            </span>
          </div>
          <div className="native-document-title">{shape.props.name}</div>
          <div
            className="native-document-preview"
            inert
            dangerouslySetInnerHTML={{
              __html: nativeDocumentHtml(shape.props.richText),
            }}
          />
          <footer className="native-document-footer">
            {access.canEdit && !shape.isLocked ? (
              <button
                type="button"
                onPointerDown={(e) => e.stopPropagation()}
                onClick={() => start()}
                title="Double-click the page to write"
              >
                Edit on canvas
              </button>
            ) : (
              <span>Document</span>
            )}
            <button
              type="button"
              onPointerDown={(e) => e.stopPropagation()}
              onClick={expand}
              aria-label={
                continues ? "Expand full document" : "Expand document"
              }
            >
              {continues ? "Full document" : "Expand"}
              <ArrowUpRight size={15} strokeWidth={1.5} />
            </button>
          </footer>
        </>
      )}
    </HTMLContainer>
  );
}

function CanvasDocumentWriter({
  shape,
  titleFirst,
  focusPoint,
  onExpand,
}: {
  shape: BoardDocument;
  titleFirst: boolean;
  focusPoint?: { x: number; y: number; bodyTop?: number };
  onExpand: () => void;
}) {
  const editor = useEditor();
  const access = useContext(DocumentAccess);
  const initial = useRef(shape).current;
  const titleInput = useRef<HTMLTextAreaElement>(null);
  const {
    textEditor,
    title,
    setTitle,
    canWrite,
    owner,
    issue,
    draft,
    unresolved,
    finishEdit,
  } = useDocumentWriting({
    editor,
    ...access,
    shape,
    initial,
    initiallyEdit: true,
    initialFocusTitle: titleFirst,
    titleInput,
    focusPoint,
  });
  function done() {
    finishEdit();
    editor.setCurrentTool("select.idle");
  }
  return (
    <div
      className="native-document-writing"
      onPointerDown={(e) => e.stopPropagation()}
      onDoubleClick={(e) => e.stopPropagation()}
      onKeyDown={(event) => {
        event.stopPropagation();
        if (event.key === "Escape") {
          event.preventDefault();
          done();
        }
      }}
    >
      <div className="native-document-masthead">
        <span>
          {String(Number(shape.meta.order ?? 0) + 1).padStart(2, "0")}
        </span>
      </div>
      <textarea
        ref={titleInput}
        rows={1}
        className="native-document-title"
        aria-label="Document title"
        value={title}
        maxLength={120}
        readOnly={!canWrite}
        onChange={(event) => {
          setTitle(event.target.value);
          if (canWrite)
            editor.updateShape<BoardDocument>({
              id: shape.id,
              type: "document",
              props: { name: event.target.value },
            });
        }}
        onBlur={() => {
          if (!title.trim() && canWrite) {
            setTitle("Untitled document");
            editor.updateShape<BoardDocument>({
              id: shape.id,
              type: "document",
              props: { name: "Untitled document" },
            });
          }
        }}
        onKeyDown={(event) => {
          if (event.key === "Enter") {
            event.preventDefault();
            textEditor?.commands.setTextSelection(1);
            textEditor?.view.focus();
          }
        }}
      />
      <div className="native-document-formatting">
        <DocumentFormatting editor={textEditor} disabled={!canWrite} />
      </div>
      <div
        className="native-document-writing-scroll"
        onWheel={(e) => e.stopPropagation()}
      >
        {issue && (
          <p className="native-document-notice" role="alert">
            {issue}
          </p>
        )}
        {unresolved && draft && (
          <div className="native-document-notice">
            <span>A different draft is on this device.</span>
            <button
              type="button"
              disabled={!access.connected || !access.canEdit}
              onClick={() => {
                done();
                const id = createWorkspaceDocument(editor, undefined, {
                  name: `${draft.name} — recovered`,
                  richText: draft.richText,
                });
                editor.setEditingShape(id);
                editor.setCurrentTool("select.editing_shape");
              }}
            >
              Recover copy
            </button>
            <button
              type="button"
              onClick={() =>
                void navigator.clipboard.writeText(
                  documentMarkdown({
                    ...initial,
                    props: {
                      ...initial.props,
                      name: draft.name,
                      richText: draft.richText,
                    },
                  }),
                )
              }
            >
              Copy draft
            </button>
          </div>
        )}
        <EditorContent
          editor={textEditor}
          className="document-writing"
          data-empty={!nativeDocumentPlaintext(shape.props.richText)}
        />
      </div>
      <footer className="native-document-footer">
        <span role="status">
          {!access.connected
            ? "Offline"
            : owner && owner !== access.sessionId
              ? "Another editor is writing"
              : canWrite
                ? "Writing · live"
                : "Opening…"}
        </span>
        <div>
          <button type="button" onClick={onExpand} aria-label="Expand document">
            <ArrowUpRight size={15} />
          </button>
          <button type="button" onClick={done}>
            <Check size={13} />
            Done
          </button>
        </div>
      </footer>
    </div>
  );
}
